import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Film, Tv, Wifi, Sparkles, Megaphone } from "lucide-react";
import { api } from "@/lib/api";
import HeroCarousel from "@/components/media/HeroCarousel";
import ContentRow from "@/components/media/ContentRow";
import ContentCard from "@/components/media/ContentCard";
import CreatorCTA from "@/components/media/CreatorCTA";
import NewsletterSignup from "@/components/media/NewsletterSignup";
import WhereToWatch from "@/components/media/WhereToWatch";
import TrailerModal from "@/components/media/TrailerModal";
import SEO from "@/components/seo/SEO";

export default function Home() {
  const [titles, setTitles] = useState([]);
  const [trailerOpen, setTrailerOpen] = useState(false);

  useEffect(() => {
    api.get("/titles?limit=200").then(({ data }) => setTitles(data)).catch(() => {});
  }, []);

  // Show titles with real cover art first, then gradient-fallback titles after.
  // Nothing is hidden — pretty artwork just gets premium placement in the featured rows.
  const withPosterFirst = (arr) =>
    [...arr].sort((a, b) => (b.poster_url ? 1 : 0) - (a.poster_url ? 1 : 0));

  const featuredMovies = useMemo(
    () => withPosterFirst(titles.filter((t) => t.type === "movie")).slice(0, 12),
    [titles]
  );
  const featuredShows = useMemo(
    () => withPosterFirst(titles.filter((t) => t.type === "series")).slice(0, 12),
    [titles]
  );
  const poolside = useMemo(() => titles.find((t) => t.slug === "poolside-cooking"), [titles]);

  return (
    <div data-testid="home-page">
      <SEO
        title="Home"
        description="Your Channel Network — a woman-owned streaming network from Virginia Beach. Watch original series, movies, documentaries, live TV, and Poolside Cooking. Your Story. Your Voice. Your Channel."
        path="/"
      />
      <HeroCarousel />

      {/* Poolside Cooking spotlight (compact, narrower) */}
      {poolside && (
        <section data-testid="poolside-spotlight" className="mt-10 px-6 md:px-12 flex justify-center">
          <div className="block w-full max-w-3xl relative overflow-hidden rounded-2xl border border-white/10 group">
            <div className="absolute inset-0 bg-gradient-to-br from-[#0E7490] via-[#0B0B0F] to-[#EA580C]" />
            <div className="absolute inset-0 opacity-55" style={{ backgroundImage: "radial-gradient(circle at 20% 30%, rgba(6,182,212,0.4), transparent 55%), radial-gradient(circle at 80% 70%, rgba(249,115,22,0.4), transparent 55%)" }} />
            <div className="relative p-5 md:p-6 grid grid-cols-[1fr,180px] md:grid-cols-[1fr,220px] gap-4 md:gap-5 items-center">
              <div className="min-w-0">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 backdrop-blur text-[11px] md:text-[12px] uppercase tracking-[0.25em] text-white/90 mb-2.5">
                  <span className="font-bold ycn-gradient-text">YCN Original</span> · Lifestyle · Cooking
                </div>
                <Link to={`/title/${poolside.slug}`} aria-label="Poolside Cooking — YCN Original">
                  <h3 className="font-display text-2xl md:text-3xl font-black uppercase leading-[0.95] tracking-tight text-white hover:underline decoration-white/40 underline-offset-4">Poolside Cooking</h3>
                </Link>
                <p className="mt-2 text-white/85 text-sm max-w-md leading-relaxed">
                  Good food, good vibes, and real conversations with celebrity guests, entertainers, chefs, and inspiring personalities.
                </p>
                <div className="mt-3.5 flex flex-wrap gap-2">
                  <button
                    type="button"
                    data-testid="poolside-trailer-btn"
                    onClick={() => setTrailerOpen(true)}
                    className="ycn-btn-primary inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full font-bold text-sm text-white transition-all"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
                    Watch Trailer
                  </button>
                </div>
              </div>
              <Link to={`/title/${poolside.slug}`} aria-label="Poolside Cooking cover">
                <div className="rounded-xl overflow-hidden aspect-[3/4] w-full border border-white/15 shadow-2xl">
                  <img src={poolside.poster_url || "/covers/poolside-cooking.jpg"} alt="Poolside Cooking" className="w-full h-full object-cover" />
                </div>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Featured Movies */}
      {featuredMovies.length > 0 && (
        <ContentRow title="Featured Movies" testId="row-featured-movies">
          {featuredMovies.map((t) => (
            <div key={t.id} className="w-[170px] md:w-[200px] shrink-0">
              <ContentCard title={t} />
            </div>
          ))}
        </ContentRow>
      )}

      {/* Featured Shows */}
      {featuredShows.length > 0 && (
        <ContentRow title="Featured Shows" testId="row-featured-shows">
          {featuredShows.map((t) => (
            <div key={t.id} className="w-[170px] md:w-[200px] shrink-0">
              <ContentCard title={t} />
            </div>
          ))}
        </ContentRow>
      )}

      {/* Awards promo banner */}
      <section className="mt-20 md:mt-24 px-6 md:px-12" data-testid="awards-promo">
        <Link to="/awards" aria-label="First Annual YCN Awards — May 1, 2027" className="block max-w-[1400px] mx-auto relative overflow-hidden rounded-3xl border border-white/10 group">
          <div className="absolute inset-0 bg-gradient-to-r from-[#1E1B4B] via-[#1E3A8A] to-[#7C2D12]" />
          <div className="absolute inset-0 opacity-60" style={{ backgroundImage: "radial-gradient(circle at 20% 30%, rgba(147,51,234,0.5), transparent 55%), radial-gradient(circle at 80% 70%, rgba(249,115,22,0.4), transparent 55%)" }} />
          <div className="relative p-10 md:p-14 flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <div className="text-[11px] uppercase tracking-[0.35em] text-white/70 mb-2">Save the Date</div>
              <div className="font-display text-3xl md:text-5xl font-black leading-tight">First Annual <span className="ycn-gradient-text">YCN Awards</span></div>
              <div className="mt-2 text-white/80">May 1, 2027 · Marriott Virginia Beach Oceanfront Resort · Virginia Beach, Virginia</div>
            </div>
            <div className="shrink-0">
              <span className="ycn-btn-primary inline-flex items-center gap-2 px-8 py-4 rounded-full font-bold text-white transition-all">Explore the Event</span>
            </div>
          </div>
        </Link>
      </section>

      <CreatorCTA />

      {/* Advertise With Us CTA */}
      <section className="mt-16 md:mt-20 px-6 md:px-12" data-testid="advertise-cta">
        <Link to="/advertise" onClick={() => window.scrollTo({ top: 0, behavior: "auto" })} aria-label="Advertise With YCN" className="block max-w-[1400px] mx-auto relative overflow-hidden rounded-3xl border border-white/10 group">
          <div className="absolute inset-0 bg-gradient-to-r from-[#0B0B0F] via-[#1E1B4B] to-[#7C2D12]" />
          <div className="absolute inset-0 opacity-55" style={{ backgroundImage: "radial-gradient(circle at 15% 30%, rgba(124,58,237,0.45), transparent 55%), radial-gradient(circle at 85% 65%, rgba(249,115,22,0.45), transparent 55%)" }} />
          <div className="relative p-8 md:p-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div className="max-w-2xl">
              <div className="inline-flex items-center gap-2 px-2.5 py-0.5 rounded-full bg-white/10 backdrop-blur text-[10px] uppercase tracking-[0.3em] text-white/85 mb-3">
                <Megaphone size={11} className="text-orange-300" /> Advertise With Us
              </div>
              <h3 className="font-display text-3xl md:text-5xl font-black uppercase leading-[0.95] tracking-tight">
                Put Your Brand in the <span className="ycn-gradient-text">Spotlight</span>
              </h3>
              <p className="mt-4 text-white/85 text-sm md:text-base leading-relaxed">
                Reach an engaged audience across the YCN app, website, original shows, movies, social content, and live events. Custom advertising and partnership packages built around your goals.
              </p>
            </div>
            <div className="shrink-0">
              <span data-testid="home-advertise-cta-btn" className="ycn-btn-primary inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-bold text-white transition-all">
                Start Your Inquiry <Megaphone size={15} />
              </span>
            </div>
          </div>
        </Link>
      </section>

      <NewsletterSignup />

      {/* Where to Watch (bottom, full) */}
      <WhereToWatch variant="bottom" testId="where-to-watch-bottom" />

      {/* Trailer modal (Poolside Cooking) */}
      <TrailerModal
        open={trailerOpen}
        onClose={() => setTrailerOpen(false)}
        title="Poolside Cooking — Trailer"
        src="/media/poolside-trailer.mp4"
        poster={poolside?.poster_url || "/covers/poolside-cooking.jpg"}
      />
    </div>
  );
}
